import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../hive/ui/Icon';
import { useFairTracker } from '../data/FairTrackerStore';
import { Empty, Input, useToast } from '../ui';
import { newChecklistItem } from '../data/entities';
import { CHECK_DEFAULT } from '../data/constants';
import { checklistProgress } from '../rules';
import { p } from '../routes';
import { tr } from '../i18n';

/**
 * Pre-trip checklist (spec § 5.10) — per fair, seeded with 15 defaults the
 * first time it is opened.
 *
 * The defaults are not generic travel advice: a VPN tested BEFORE flying, a
 * China SIM, RMB cash with WeChat Pay set up, the forwarder's Chinese address
 * saved in the app, and a backup exported before leaving. Each one is something
 * that is painful or impossible to fix once you have landed.
 */
export default function ChecklistScreen() {
  const toast = useToast();
  const { fair, saveFair } = useFairTracker();
  const [text, setText] = useState('');
  const seeded = useRef(false);

  /* Seed on first open. Guarded by a ref as well as the length check, because
     saveFair triggers a reload and this effect would otherwise re-enter before
     the new state has landed. */
  useEffect(() => {
    if (!fair || seeded.current) return;
    if ((fair.checklist || []).length) return;
    seeded.current = true;
    saveFair({ ...fair, checklist: CHECK_DEFAULT.map(newChecklistItem) }).catch(() => {
      seeded.current = false;
    });
  }, [fair, saveFair]);

  if (!fair) {
    return (
      <div className="ft-page">
        <Empty title={tr("Open one fair first")} action={<Link className="ft-btn" to={p('/fairs')}>{tr("My fairs")}</Link>} />
      </div>
    );
  }

  const list = fair.checklist || [];
  const progress = checklistProgress(fair);

  const write = async (checklist) => {
    try {
      await saveFair({ ...fair, checklist });
    } catch (e) {
      toast('Could not save', 'error');
    }
  };

  const toggle = (id) => write(list.map((i) => (i.id === id ? { ...i, done: !i.done } : i)));
  const remove = (id) => write(list.filter((i) => i.id !== id));

  const add = async () => {
    const t = text.trim();
    if (!t) return;
    setText('');
    await write([...list, newChecklistItem(t)]);
  };

  const reset = async () => {
    seeded.current = true;
    await write(CHECK_DEFAULT.map(newChecklistItem));
    toast('Reset to the default list');
  };

  return (
    <div className="ft-page">
      <div className="ft-title">
        <h1 className="ft-h1">{tr("Pre-trip checklist")}</h1>
        <p className="ft-sub" style={{ margin: 0 }}>
          {fair.name} ·{' '}
          <b>
            {progress.done}/{progress.total} done
          </b>
        </p>
      </div>

      {!list.length && <Empty title={tr("Nothing on the list")}>{tr("Add an item, or reset to the defaults.")}</Empty>}

      <ul className="ft-checklist">
        {list.map((item) => (
          <li key={item.id} className={item.done ? 'ft-checked' : undefined}>
            <label>
              <input type="checkbox" checked={!!item.done} onChange={() => toggle(item.id)} />
              <span>{item.text}</span>
            </label>
            <button type="button" className="ft-icon-btn" onClick={() => remove(item.id)} aria-label="Remove item">
              <Icon name="X" size={15} />
            </button>
          </li>
        ))}
      </ul>

      <div className="ft-row" style={{ marginTop: 12 }}>
        <Input
          value={text}
          onChange={setText}
          placeholder={tr("Add an item…")}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
        />
        <button type="button" className="ft-btn ft-btn-primary" style={{ flex: '0 0 auto' }} onClick={add}>
          {tr("Add")}
        </button>
      </div>

      <button type="button" className="ft-btn ft-btn-block" style={{ marginTop: 8 }} onClick={reset}>
        {tr("Reset to default list")}
      </button>
    </div>
  );
}
